/** biome-ignore-all lint/performance/noNamespaceImport: ok */
"use client";
import { Fieldset } from "@base-ui/react/fieldset";
import { Form } from "@base-ui/react/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import cake from "#/assets/images/cake.svg";
import PageHeader from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
import * as Field from "@/components/ui/form/field";
import * as Radio from "@/components/ui/form/radio";
import { RadioGroup } from "@/components/ui/form/radio-group";
import { CalculateDueDateAction } from "../_components/action";
import {
  type BirthDateModel,
  type BirthDateResponse,
  BirthDateSchema,
} from "../_components/types";
import { ResultDialog } from "./_components/result-dialog";

const DueDateCalculation = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<BirthDateResponse | null>(null);

  const { control, handleSubmit } = useForm<BirthDateModel>({
    defaultValues: {
      mode: "1",
      referenceDate: "",
    },
    resolver: zodResolver(BirthDateSchema),
  });

  const mutation = useMutation({
    mutationFn: async (data: BirthDateModel) => {
      return await CalculateDueDateAction(data);
    },
    onSuccess: (resp) => {
      if (resp.isSuccess) {
        setResult(resp.result);
        setDialogOpen(true);
      }
    },
  });

  const onSubmit = (data: BirthDateModel) => {
    mutation.mutate(data);
  };
  return (
    <div className="flex flex-col gap-4 justify-center mb-20">
      <PageHeader title="Yumurtlama Günü Hesaplama" />
      <div className="rounded-full border border-gray-300 size-56 mx-auto mb-10">
        <ExtendedImage
          alt="ovum"
          className="rounded-full size-56 "
          height={224}
          src={cake}
          width={224}
        />
      </div>
      <Form
        className="w-96 mx-auto flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="mode"
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { invalid, isTouched, isDirty },
          }) => (
            <Field.Root
              dirty={isDirty}
              invalid={invalid}
              name={name}
              touched={isTouched}
            >
              <Fieldset.Root
                render={
                  <RadioGroup
                    className="flex flex-row justify-between items-center"
                    inputRef={ref}
                    onValueChange={onChange}
                    value={value}
                  />
                }
              >
                <Field.Item className="border border-gray-300 rounded-2xl p-2 size-28 flex flex-col justify-center items-center text-center">
                  <Field.Label>
                    <Radio.Root
                      className="place-self-end"
                      onBlur={onBlur}
                      value="1"
                    >
                      <Radio.Indicator />
                    </Radio.Root>
                    Gebe Kalma Tarihine Göre
                  </Field.Label>
                </Field.Item>
                <Field.Item className="border border-gray-300 rounded-2xl p-2 size-28 flex flex-col justify-center items-center text-center">
                  <Field.Label>
                    <Radio.Root
                      className="place-self-end"
                      onBlur={onBlur}
                      value="2"
                    >
                      <Radio.Indicator />
                    </Radio.Root>
                    Son Adet Başlangıç Tarihine Göre
                  </Field.Label>
                </Field.Item>
                <Field.Item className="border border-gray-300 rounded-2xl p-2 size-28 flex flex-col justify-center items-center text-center">
                  <Field.Label>
                    <Radio.Root
                      className="place-self-end"
                      onBlur={onBlur}
                      value="3"
                    >
                      <Radio.Indicator />
                    </Radio.Root>
                    Embrio Transfer Tarihine Göre
                  </Field.Label>
                </Field.Item>
              </Fieldset.Root>
            </Field.Root>
          )}
        />
        <Controller
          control={control}
          name="referenceDate"
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { invalid, isTouched, isDirty, error },
          }) => (
            <Field.Root
              className=""
              dirty={isDirty}
              invalid={invalid}
              name={name}
              touched={isTouched}
            >
              <Field.Control
                className="w-full max-w-full!"
                onBlur={onBlur}
                onValueChange={onChange}
                ref={ref}
                type="date"
                value={value}
              />
              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
        />
        <Button disabled={mutation.isPending} type="submit">
          {mutation.isPending ? "Hesaplanıyor..." : "Hesapla"}
        </Button>
      </Form>
      <ResultDialog
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        result={result}
      />
    </div>
  );
};

export default DueDateCalculation;
